import { useRef, useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/core';
import cx from 'classnames';

const useStyles = makeStyles((theme) => ({
  toggleGroup: {
    position: 'relative',
    display: 'inline-flex',
    backgroundColor: 'rgba(0, 0, 0, 0.04)',
    borderRadius: 12,
    padding: 4,
    gap: 4,
  },
  slideIndicator: {
    position: 'absolute',
    top: 4,
    left: 4,
    height: 'calc(100% - 8px)',
    backgroundColor: '#ffffff',
    borderRadius: 8,
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
    transition: 'all 0.3s cubic-bezier(0.4, 0.0, 0.2, 1)',
    zIndex: 1,
  },
  toggleButton: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '6px 18px',
    border: 'none',
    background: 'transparent',
    borderRadius: 8,
    fontSize: '16px',
    fontWeight: 500,
    color: theme.palette.text.secondary,
    cursor: 'pointer',
    transition: 'color 0.2s ease',
    outline: 'none',
    zIndex: 2,
    '&:hover': {
      color: theme.palette.text.primary,
    },
    '&:focus': {
      boxShadow: '0 0 0 2px rgba(25, 118, 210, 0.2)',
    },
  },
  selected: {
    color: `${theme.palette.primary.main} !important`,
  },
  icon: {
    width: 20,
    height: 20,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '& svg': {
      width: '100%',
      height: '100%',
    },
  },
}));

const ToggleButtonGroup = ({ value, onChange, options, name }) => {
  const classes = useStyles();
  const groupRef = useRef(null);
  const [indicatorStyle, setIndicatorStyle] = useState({});
  const buttonsRef = useRef({});

  useEffect(() => {
    updateIndicator();
  }, [value]);

  const updateIndicator = () => {
    if (!groupRef.current) return;

    const selectedButton = buttonsRef.current[value];
    if (selectedButton) {
      const groupRect = groupRef.current.getBoundingClientRect();
      const buttonRect = selectedButton.getBoundingClientRect();

      setIndicatorStyle({
        width: `${buttonRect.width}px`,
        transform: `translateX(${buttonRect.left - groupRect.left - 4}px)`,
      });
    }
  };

  const handleClick = (optionValue) => {
    if (onChange && optionValue !== value) {
      onChange(optionValue);
    }
  };

  // Initialize indicator on mount
  useEffect(() => {
    // Small delay to ensure DOM is ready
    setTimeout(updateIndicator, 100);

    // Update on window resize
    window.addEventListener('resize', updateIndicator);
    return () => window.removeEventListener('resize', updateIndicator);
  }, []);

  return (
    <div ref={groupRef} className={classes.toggleGroup} role="radiogroup" aria-label={name}>
      <div className={classes.slideIndicator} style={indicatorStyle} />
      {options.map((option) => (
        <button
          ref={(el) => buttonsRef.current[option.value] = el}
          key={option.value}
          type="button"
          role="radio"
          aria-checked={value === option.value}
          className={cx(classes.toggleButton, {
            [classes.selected]: value === option.value,
          })}
          onClick={() => handleClick(option.value)}
        >
          {option.icon && <span className={classes.icon}>{option.icon}</span>}
          {option.label}
        </button>
      ))}
    </div>
  );
};

export default ToggleButtonGroup;
